/**
 * ArticleControl
 * Renders structured article/rich text content.
 * Accepts either HTML or Markdown in the `content` field.
 * Markdown is detected and converted to HTML before sanitization.
 * Security: All HTML content is sanitized using DOMPurify with a strict whitelist.
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useMemo, memo } from 'react';
import DOMPurify from 'dompurify';
import type { ControlRendererProps } from '../ControlRenderer';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'span', 'div', 'hr', 'img'],
  ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'class'],
  KEEP_CONTENT: true,
};

function isMarkdown(text: string): boolean {
  return /^#{1,6}\s|^\s*[-*+]\s|\*\*[^*]+\*\*|`[^`]+`/.test(text);
}

function markdownToHtml(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let inList = false;

  const closeList = () => {
    if (inList) { out.push('</ul>'); inList = false; }
  };

  const inlineRules = (s: string) =>
    s
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  for (const raw of lines) {
    const line = raw.trimEnd();

    const hMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (hMatch) {
      closeList();
      const level = hMatch[1].length;
      out.push(`<h${level}>${inlineRules(hMatch[2])}</h${level}>`);
      continue;
    }

    const liMatch = line.match(/^\s*[-*+]\s+(.+)$/);
    if (liMatch) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inlineRules(liMatch[1])}</li>`);
      continue;
    }

    closeList();

    if (line.trim() === '') {
      out.push('<br>');
    } else {
      out.push(`<p>${inlineRules(line)}</p>`);
    }
  }

  closeList();
  return out.join('');
}

export const ArticleControl = memo((props: ControlRendererProps) => {
  const { control } = props;
  const content = (control.config?.content ?? (control as any).content) as string | undefined;

  const sanitizedContent = useMemo(() => {
    if (!content) return '';
    const html = isMarkdown(content) ? markdownToHtml(content) : content;
    return DOMPurify.sanitize(html, SANITIZE_CONFIG);
  }, [content]);

  return (
    <article className="af-article-control" data-control-id={control.id}>
      {control.label && <h3 className="af-article-control__heading">{control.label}</h3>}
      {sanitizedContent ? (
        // eslint-disable-next-line react/no-danger
        <div
          className="af-article-control__body"
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      ) : (
        <p className="af-article-control__empty">No content</p>
      )}
    </article>
  );
});

ArticleControl.displayName = 'ArticleControl';

export default ArticleControl;
